
import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { User } from '@supabase/supabase-js';

interface UseInactivityLogoutProps {
  timeoutMinutes?: number;
  enabled?: boolean;
}

export const useInactivityLogout = ({ 
  timeoutMinutes = 20, 
  enabled = true 
}: UseInactivityLogoutProps = {}) => {
  const { signOut, isAdmin, user } = useAuth();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutDuration = timeoutMinutes * 60 * 1000; // Convert to milliseconds

  // Only enable if we have an authenticated user AND they are admin
  const shouldBeActive = enabled && isAdmin && user !== null;

  const resetTimeout = useCallback(() => {
    if (!shouldBeActive) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      signOut();
    }, timeoutDuration);
  }, [shouldBeActive, signOut, timeoutDuration]);

  const clearInactivityTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!shouldBeActive) {
      clearInactivityTimeout();
      return;
    }

    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Reset timeout on any activity
    const handleActivity = () => {
      resetTimeout();
    };

    // Start the initial timeout
    resetTimeout();

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup function
    return () => {
      clearInactivityTimeout();
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [shouldBeActive, resetTimeout, clearInactivityTimeout]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInactivityTimeout();
    };
  }, [clearInactivityTimeout]);

  return { clearInactivityTimeout, resetTimeout };
};
